import { supabase, uploadToStorage } from './supabaseClient';

// Utilitário para converter FormData para Objeto simples
export function parseFormData(data) {
  if (!data) return {};
  if (data instanceof FormData) {
    const obj = {};
    for (const [key, value] of data.entries()) {
      obj[key] = value;
    }
    return obj;
  }
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return { ...data };
}

// Gera um JWT válido em formato padrão para compatibilidade com jwt-decode no frontend
function createJwtToken(usuario) {
  const header = { alg: 'HS256', typ: 'JWT' };
  const payload = {
    id: usuario.id,
    email: usuario.email,
    nome: usuario.nome || usuario.nomeArtistico || usuario.nomeCompleto || '',
    role: usuario.role || 'usuario',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 1 dia de validade
  };

  const base64Url = (obj) => {
    const json = JSON.stringify(obj);
    const base64 = typeof window !== 'undefined' ? window.btoa(unescape(encodeURIComponent(json))) : Buffer.from(json).toString('base64');
    return base64.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
  };

  return `${base64Url(header)}.${base64Url(payload)}.supabase_signature`;
}

// Gerador de slug idêntico ao backend original
function gerarSlug(nome) {
  return String(nome || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// Validador de CPF
function isValidCPF(cpf) {
  if (!cpf) return false;
  const cpfLimpo = String(cpf).replace(/\D/g, '');
  if (cpfLimpo.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpfLimpo)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i += 1) soma += Number(cpfLimpo[i]) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== Number(cpfLimpo[9])) return false;

  soma = 0;
  for (let i = 0; i < 10; i += 1) soma += Number(cpfLimpo[i]) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== Number(cpfLimpo[10])) return false;

  return true;
}

export const supabaseApi = {
  // ===================== AUTENTICAÇÃO =====================
  auth: {
    async loginAdmin(email, senha) {
      if (!email || !senha || !email.trim() || !senha.trim()) {
        const error = new Error('Email e senha são obrigatórios');
        error.status = 400;
        throw error;
      }

      const { data, error } = await supabase.rpc('login_admin', {
        p_email: email.trim(),
        p_senha: senha.trim()
      });

      if (error) throw error;
      const res = data?.[0];
      if (!res || !res.sucesso) {
        const err = new Error(res?.mensagem || 'E-mail ou senha incorretos.');
        err.status = res?.mensagem === 'Usuário não encontrado' ? 404 : 401;
        throw err;
      }

      const accessToken = createJwtToken(res.usuario);
      return { accessToken, usuario: res.usuario };
    },

    async loginAuthor(email, senha) {
      if (!email || !senha || !email.trim() || !senha.trim()) {
        const error = new Error('Email e senha são obrigatórios');
        error.status = 400;
        throw error;
      }

      const { data, error } = await supabase.rpc('login_autor', {
        p_email: email.trim(),
        p_senha: senha.trim()
      });

      if (error) throw error;
      const res = data?.[0];
      if (!res || !res.sucesso) {
        const err = new Error(res?.mensagem || 'E-mail ou senha incorretos.');
        err.status = res?.mensagem === 'Usuário não encontrado' ? 404 : 401;
        throw err;
      }

      const accessToken = createJwtToken(res.usuario);
      return { accessToken, usuario: res.usuario };
    },

    async createAdminAccount(payload) {
      const raw = parseFormData(payload);
      const { nome, email, senha, confirmarSenha } = raw;

      const { data, error } = await supabase.rpc('criar_conta_admin', {
        p_nome: nome,
        p_email: email,
        p_senha: senha,
        p_confirmar_senha: confirmarSenha
      });

      if (error) throw error;
      const res = data?.[0];
      if (!res || !res.sucesso) {
        const err = new Error(res?.mensagem || 'Erro ao criar conta');
        err.status = 400;
        throw err;
      }

      return { message: 'Conta criada com sucesso.', admin: res.admin };
    }
  },

  // ===================== EVENTOS =====================
  eventos: {
    async getAll() {
      const { data, error } = await supabase
        .from('eventos')
        .select('*')
        .order('data', { ascending: true });

      if (error) throw error;
      return data || [];
    },

    async getBySlug(slug) {
      if (!slug) {
        const err = new Error('Nenhum slug foi informado.');
        err.status = 400;
        throw err;
      }

      const { data, error } = await supabase
        .from('eventos')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        const err = new Error('Nenhum evento foi encontrado.');
        err.status = 404;
        throw err;
      }

      return data;
    },

    async create(rawPayload, rawData) {
      let imagemFile = null;
      let bodyData = {};

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        imagemFile = rawData.get('imagem');
      } else {
        bodyData = parseFormData(rawPayload);
        imagemFile = bodyData.imagem;
      }

      const { nome, descricao, data, local, endereco } = bodyData;

      if (!nome || !data || !local || !endereco) {
        const err = new Error('Preencha todos os campos obrigatórios');
        err.status = 400;
        throw err;
      }

      let imageUrl = null;
      if (imagemFile && (imagemFile instanceof Blob || typeof imagemFile === 'object')) {
        imageUrl = await uploadToStorage('eventos', imagemFile);
      } else if (typeof imagemFile === 'string') {
        imageUrl = imagemFile;
      }

      const dataISO = new Date(data).toISOString();

      const slug = gerarSlug(nome);

      // Verifica duplicidade
      const { data: existing } = await supabase
        .from('eventos')
        .select('id')
        .eq('slug', slug)
        .maybeSingle();

      if (existing) {
        const err = new Error('Evento já foi criado.');
        err.status = 400;
        throw err;
      }

      const { data: newEvent, error } = await supabase
        .from('eventos')
        .insert([{
          nome,
          descricao,
          data: dataISO,
          local,
          endereco,
          slug,
          imagem: imageUrl
        }])
        .select()
        .single();

      if (error) throw error;
      return { message: 'Evento criado com sucesso.', evento: newEvent };
    },

    async update(slug, rawPayload, rawData) {
      let imagemFile = null;
      let bodyData = {};

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        imagemFile = rawData.get('imagem');
      } else {
        bodyData = parseFormData(rawPayload);
        imagemFile = bodyData.imagem;
      }

      const updateData = {};
      if (bodyData.nome) {
        updateData.nome = bodyData.nome;
        updateData.slug = gerarSlug(bodyData.nome);
      }
      if (bodyData.descricao !== undefined) updateData.descricao = bodyData.descricao;
      if (bodyData.local !== undefined) updateData.local = bodyData.local;
      if (bodyData.endereco !== undefined) updateData.endereco = bodyData.endereco;
      if (bodyData.data) {
        updateData.data = new Date(bodyData.data).toISOString();
      }

      if (imagemFile && (imagemFile instanceof Blob || typeof imagemFile === 'object')) {
        updateData.imagem = await uploadToStorage('eventos', imagemFile);
      }

      const { data: updated, error } = await supabase
        .from('eventos')
        .update(updateData)
        .eq('slug', slug)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!updated) {
        const err = new Error('Nenhum evento foi encontrado.');
        err.status = 404;
        throw err;
      }

      return updated;
    }
  },

  // ===================== AUTORES =====================
  autores: {
    async getAll() {
      const { data, error } = await supabase
        .from('autores')
        .select('*')
        .order('nomeArtistico', { ascending: true });

      if (error) throw error;
      return data || [];
    },

    async getById(id) {
      const { data, error } = await supabase
        .from('autores')
        .select('*, livros:livros(id, titulo, genero, capa, preco)')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        const err = new Error('Nenhum autor foi encontrado.');
        err.status = 404;
        throw err;
      }

      return data;
    },

    async create(rawPayload, rawData) {
      let fotoFile = null;
      let bodyData = {};

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        fotoFile = rawData.get('foto');
      } else {
        bodyData = parseFormData(rawPayload);
        fotoFile = bodyData.foto;
      }

      const { nome, nomeArtistico, email, senha, confirmarSenha, biografia, generoLiterario } = bodyData;

      if (!nome || !nomeArtistico || !email || !senha || !confirmarSenha || !biografia) {
        const err = new Error('Todos os campos são obrigatórios!');
        err.status = 400;
        throw err;
      }

      if (senha.length < 6 || senha.length > 15) {
        const err = new Error('A senha deve conter entre 6 a 15 caracteres');
        err.status = 400;
        throw err;
      }

      if (senha !== confirmarSenha) {
        const err = new Error('As senhas não correspondem!');
        err.status = 400;
        throw err;
      }

      let generos = generoLiterario;
      if (typeof generos === 'string') {
        try { generos = JSON.parse(generos); } catch { generos = [generos]; }
      }
      if (!Array.isArray(generos) || generos.length === 0) {
        const err = new Error('É obrigatório selecionar pelo menos um gênero literário.');
        err.status = 400;
        throw err;
      }

      // Upload da foto
      let fotoUrl = fotoFile;
      if (fotoFile && (fotoFile instanceof Blob || typeof fotoFile === 'object')) {
        fotoUrl = await uploadToStorage('autores', fotoFile);
      }

      if (!fotoUrl) {
        const err = new Error('Foto é obrigatória!');
        err.status = 400;
        throw err;
      }

      const { data: newAuthor, error } = await supabase
        .from('autores')
        .insert([{
          nome,
          nomeArtistico,
          email: email.trim().toLowerCase(),
          senha, // O trigger do PostgreSQL aplicará o hash bcrypt automaticamente
          foto: fotoUrl,
          biografia,
          generoLiterario: generos,
          role: 'autor'
        }])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          const err = new Error('Autor já cadastrado');
          err.status = 409;
          throw err;
        }
        throw error;
      }

      return { message: 'Autor cadastrado com sucesso.', autor: newAuthor };
    },

    async completeInfo(payload) {
      const raw = parseFormData(payload);
      const { id, endereco, telefone, cpf } = raw;

      if (cpf && !isValidCPF(cpf)) {
        const err = new Error('CPF não é válido. Por favor, verifique.');
        err.status = 400;
        throw err;
      }

      const { data, error } = await supabase
        .from('autores')
        .update({ endereco, telefone, cpf: cpf ? String(cpf).replace(/\D/g, '') : null })
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        const err = new Error('Autor não encontrado');
        err.status = 401;
        throw err;
      }

      return { message: 'Informações do autor adicionadas com sucesso.' };
    }
  },

  // ===================== LIVROS =====================
  livros: {
    async getAll() {
      const { data, error } = await supabase
        .from('livros')
        .select('*, autor:autores!autor_id(id, "nomeArtistico", "generoLiterario", foto)')
        .order('titulo', { ascending: true });

      if (error) throw error;
      return data || [];
    },

    async getById(id) {
      const { data, error } = await supabase
        .from('livros')
        .select('*, autor:autores!autor_id(id, "nomeArtistico", "generoLiterario", foto, telefone)')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        const err = new Error('Nenhum livro foi encontrado.');
        err.status = 404;
        throw err;
      }

      return data;
    },

    async create(rawPayload, rawData) {
      let capaFile = null;
      let bodyData = {};

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        capaFile = rawData.get('capa');
      } else {
        bodyData = parseFormData(rawPayload);
        capaFile = bodyData.capa;
      }

      const { titulo, genero, anoLancamento, preco, sinopse, tipo_venda, link_venda, autor_id } = bodyData;

      if (!titulo || !genero || !anoLancamento || !preco || !sinopse || !autor_id || !tipo_venda) {
        const err = new Error('Todos os campos são obrigatórios!');
        err.status = 400;
        throw err;
      }

      let capaUrl = capaFile;
      if (capaFile && (capaFile instanceof Blob || typeof capaFile === 'object')) {
        capaUrl = await uploadToStorage('livros', capaFile);
      }

      if (!capaUrl) {
        const err = new Error('Capa é obrigatória!');
        err.status = 400;
        throw err;
      }

      const { data: newBook, error } = await supabase
        .from('livros')
        .insert([{
          titulo,
          genero,
          anoLancamento: Number(anoLancamento),
          preco: Number(preco),
          sinopse,
          capa: capaUrl,
          tipo_venda,
          link_venda: link_venda || null,
          autor_id
        }])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          const err = new Error('Livro já cadastrado');
          err.status = 409;
          throw err;
        }
        throw error;
      }

      return { message: 'Livro cadastrado com sucesso.', livro: newBook };
    }
  },

  // ===================== ARTISTAS =====================
  artistas: {
    async getAll() {
      const { data, error } = await supabase
        .from('artistas')
        .select('*')
        .order('id', { ascending: false });

      if (error) throw error;
      // Remove campos sensíveis da listagem pública
      return (data || []).map((art) => {
        const { senha, ...resto } = art;
        return resto;
      });
    },

    async getById(id) {
      const { data, error } = await supabase
        .from('artistas')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        const err = new Error('Artista não encontrado.');
        err.status = 404;
        throw err;
      }

      const { senha, ...resto } = data;
      return resto;
    },

    async create(rawPayload, rawData) {
      let curriculoFile = null;
      let fotoFile = null;
      let bodyData = {};

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        curriculoFile = rawData.get('linkCurriculo');
        fotoFile = rawData.get('fotoArtista');
      } else {
        bodyData = parseFormData(rawPayload);
        curriculoFile = bodyData.linkCurriculo;
        fotoFile = bodyData.fotoArtista;
      }

      if (curriculoFile && (curriculoFile instanceof Blob || typeof curriculoFile === 'object')) {
        bodyData.linkCurriculo = await uploadToStorage('artistas', curriculoFile);
      }
      if (fotoFile && (fotoFile instanceof Blob || typeof fotoFile === 'object')) {
        bodyData.fotoArtista = await uploadToStorage('artistas', fotoFile);
      }

      // Normaliza booleans e arrays
      const normalizeBool = (v) => v === true || v === 'true';
      const cpfLimpo = String(bodyData.cpf || '').replace(/\D/g, '');

      if (!isValidCPF(cpfLimpo)) {
        const err = new Error('CPF inválido.');
        err.status = 400;
        throw err;
      }

      if (bodyData.senha && bodyData.confirmarSenha && bodyData.senha !== bodyData.confirmarSenha) {
        const err = new Error('As senhas não coincidem.');
        err.status = 400;
        throw err;
      }

      let linguagem = bodyData.linguagemArtistica;
      if (typeof linguagem === 'string') {
        try { linguagem = JSON.parse(linguagem); } catch { linguagem = linguagem.split(',').map(s => s.trim()); }
      }

      const insertPayload = {
        nomeCompleto: bodyData.nomeCompleto,
        nomeSocial: bodyData.nomeSocial || null,
        nomeArtistico: bodyData.nomeArtistico || null,
        cpf: cpfLimpo,
        dataDeNascimento: bodyData.dataDeNascimento,
        nomeDoPai: bodyData.nomeDoPai || null,
        nomeDaMae: bodyData.nomeDaMae,
        genero: bodyData.genero,
        escolaridade: bodyData.escolaridade,
        endereco: bodyData.endereco,
        numero: Number(bodyData.numero || 0),
        bairro: bodyData.bairro,
        cep: bodyData.cep,
        cidade: bodyData.cidade,
        estado: bodyData.estado,
        moradorDoMunicipio: normalizeBool(bodyData.moradorDoMunicipio),
        celular: bodyData.celular,
        email: bodyData.email,
        senha: bodyData.senha, // Trigger aplica hash
        linguagemArtistica: Array.isArray(linguagem) ? linguagem : [],
        rendaIndividualExclusivoDoTrabalhoArtistico: normalizeBool(bodyData.rendaIndividualExclusivoDoTrabalhoArtistico),
        outrasRendas: bodyData.outrasRendas || null,
        participaDeOrganizacao: bodyData.participaDeOrganizacao || null,
        vinculoFUNDARPE: normalizeBool(bodyData.vinculoFUNDARPE),
        tocouTocaNaFUNDARPE: normalizeBool(bodyData.tocouTocaNaFUNDARPE),
        espacoApresentacao: bodyData.espacoApresentacao || null,
        tempoDeAtividadeArtistica: bodyData.tempoDeAtividadeArtistica,
        apresentacaoPorSemanada: bodyData.apresentacaoPorSemanada ? Number(bodyData.apresentacaoPorSemanada) : null,
        linksDeMaterias: bodyData.linksDeMaterias || null,
        linkPaginaDoArtista: bodyData.linkPaginaDoArtista || null,
        linkCurriculo: bodyData.linkCurriculo,
        fotoArtista: bodyData.fotoArtista,
        banco: bodyData.banco,
        agencia: Number(bodyData.agencia || 0),
        conta: Number(bodyData.conta || 0),
        tipoDeConta: bodyData.tipoDeConta
      };

      const { data: newArtist, error } = await supabase
        .from('artistas')
        .insert([insertPayload])
        .select()
        .single();

      if (error) throw error;
      const { senha: _, ...safeArtist } = newArtist;
      return {
        success: true,
        message: 'Artista cadastrado com sucesso.',
        data: safeArtist
      };
    },

    async update(id, rawPayload, rawData) {
      let curriculoFile = null;
      let fotoFile = null;
      let bodyData = {};

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        curriculoFile = rawData.get('linkCurriculo');
        fotoFile = rawData.get('fotoArtista');
      } else {
        bodyData = parseFormData(rawPayload);
        curriculoFile = bodyData.linkCurriculo;
        fotoFile = bodyData.fotoArtista;
      }

      if (curriculoFile && (curriculoFile instanceof Blob || typeof curriculoFile === 'object')) {
        bodyData.linkCurriculo = await uploadToStorage('artistas', curriculoFile);
      }
      if (fotoFile && (fotoFile instanceof Blob || typeof fotoFile === 'object')) {
        bodyData.fotoArtista = await uploadToStorage('artistas', fotoFile);
      }

      const updateData = { ...bodyData };
      delete updateData.id;
      delete updateData.confirmarSenha;

      const { data: updated, error } = await supabase
        .from('artistas')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!updated) {
        const err = new Error('Artista não encontrado.');
        err.status = 404;
        throw err;
      }

      const { senha: _, ...safeArtist } = updated;
      return {
        success: true,
        message: 'Artista atualizado com sucesso.',
        data: safeArtist
      };
    },

    async delete(id) {
      const { error } = await supabase
        .from('artistas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { success: true, message: 'Artista removido com sucesso.' };
    }
  },

  // ===================== CONCURSO DE POEMA =====================
  poema: {
    async register(rawPayload, rawData) {
      let bodyData = {};
      let comprovanteFile = null;
      let documentoFile = null;
      let poemaArquivoFile = null;

      if (rawData instanceof FormData) {
        bodyData = parseFormData(rawData);
        comprovanteFile = rawData.get('comprovante_endereco');
        documentoFile = rawData.get('documento');
        poemaArquivoFile = rawData.get('poemaArquivo');
      } else {
        bodyData = parseFormData(rawPayload);
        comprovanteFile = bodyData.comprovante_endereco;
        documentoFile = bodyData.documento;
        poemaArquivoFile = bodyData.poemaArquivo;
      }

      const {
        categoria,
        nome,
        telefone,
        endereco,
        responsavel,
        nome_responsavel,
        poemaTexto,
        Estilo,
        estilo
      } = bodyData;

      const estiloFinal = Estilo || estilo;

      if (!categoria || !nome || !telefone || !endereco) {
        const err = new Error('Campos obrigatórios (categoria, nome, telefone, endereco) não preenchidos.');
        err.status = 400;
        throw err;
      }

      // Uploads dos arquivos para o bucket concurso-poema
      let comprovanteUrl = comprovanteFile;
      if (comprovanteFile && (comprovanteFile instanceof Blob || typeof comprovanteFile === 'object')) {
        comprovanteUrl = await uploadToStorage('concurso-poema', comprovanteFile);
      }

      let documentoUrl = documentoFile;
      if (documentoFile && (documentoFile instanceof Blob || typeof documentoFile === 'object')) {
        documentoUrl = await uploadToStorage('concurso-poema', documentoFile);
      }

      let poemaArquivoUrl = poemaArquivoFile;
      if (poemaArquivoFile && (poemaArquivoFile instanceof Blob || typeof poemaArquivoFile === 'object')) {
        poemaArquivoUrl = await uploadToStorage('concurso-poema', poemaArquivoFile);
      }

      if (!comprovanteUrl || !documentoUrl) {
        const err = new Error('Comprovante de endereço e documento são obrigatórios.');
        err.status = 400;
        throw err;
      }

      // Regras de negócio do concurso
      const { data: existingPoems } = await supabase
        .from('concursoPoema')
        .select('*')
        .eq('nome', nome)
        .eq('telefone', telefone);

      const poemsList = existingPoems || [];

      if (categoria === 'Arlindo Lopes') {
        const arlindo = poemsList.filter(p => p.categoria === 'Arlindo Lopes');
        if (arlindo.length >= 1) {
          const err = new Error('Você já atingiu o limite de inscrições (1) para a categoria Arlindo Lopes.');
          err.status = 400;
          throw err;
        }
      } else if (categoria === 'Lindoaldo Campos' || categoria === 'Vinícius Gregório') {
        if (!estiloFinal || !estiloFinal.trim()) {
          const err = new Error('O campo estilo é obrigatório para esta categoria.');
          err.status = 400;
          throw err;
        }

        const catPoems = poemsList.filter(p => p.categoria === categoria);
        if (catPoems.length >= 3) {
          const err = new Error(`Você já atingiu o limite de inscrições (3) para a categoria ${categoria}.`);
          err.status = 400;
          throw err;
        }

        const duplicateStyle = catPoems.find(
          p => p.Estilo && p.Estilo.trim().toLowerCase() === estiloFinal.trim().toLowerCase()
        );
        if (duplicateStyle) {
          const err = new Error(`Você já registrou um poema com o estilo '${estiloFinal}' na categoria ${categoria}.`);
          err.status = 400;
          throw err;
        }
      }

      const { data: newEntry, error } = await supabase
        .from('concursoPoema')
        .insert([{
          categoria,
          nome,
          telefone,
          endereco,
          comprovante_endereco: comprovanteUrl,
          documento: documentoUrl,
          responsavel: responsavel || null,
          nome_responsavel: nome_responsavel || null,
          Estilo: estiloFinal || null,
          poemaTexto: poemaTexto || null,
          poemaArquivo: poemaArquivoUrl || null
        }])
        .select()
        .single();

      if (error) throw error;
      return { message: 'Inscrição registrada com sucesso.', data: newEntry };
    }
  }
};
